<template>
  <v-dialog v-model="showDialog" width="700" persistent>
    <v-card>
      <v-card-title class="secondary white--text">
        {{ chargeDetails.id ? "Edit Charge" : "Add Charge" }}
        <v-spacer></v-spacer>
        <v-btn icon @click="resetForm(), (showDialog = false)">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-5">
        <v-form ref="chargeForm" v-model="isValid" lazy-validation>
          <v-row class="ma-0">
            <v-col cols="12" lg="6" class="Form-Columns-Dense">
              <MultiSelect
                label="Charge *"
                :items="allCharges"
                itemText="charge_title"
                itemValue="id"
                :returnObject="true"
                :rules="[(v) => !!v || 'Charge is Required']"
                hide-details="auto"
                v-model="selectedCharge"
                :error-messages="formErrors.charge"
                @input="
                  delete formErrors.charge;
                  setChargeDetails($event);
                "
              />
            </v-col>
            <v-col cols="12" lg="6" class="Form-Columns-Dense">
              <MultiSelect
                label="Tax Type *"
                :items="taxTypeList"
                itemText="display_name"
                itemValue="value"
                hide-details="auto"
                v-model="chargeDetails.tax_type"
                :error-messages="formErrors.tax_type"
              />
            </v-col>
            <v-col cols="12" lg="6" class="Form-Columns-Dense">
              <InputField
                label="Description"
                readonly
                :clearable="false"
                hide-details="auto"
                v-model="chargeDetails.description"
              />
            </v-col>
            <v-col cols="12" lg="6" class="Form-Columns-Dense">
              <InputField
                label="GL No."
                readonly
                :clearable="false"
                hide-details="auto"
                v-model="chargeDetails.gl_no"
              />
            </v-col>
            <v-col cols="12" lg="4" class="Form-Columns-Dense">
              <InputField
                label="Charge Type"
                readonly
                :clearable="false"
                hide-details="auto"
                v-model="chargeDetails.da_charge_type"
              />
            </v-col>
            <v-col cols="12" lg="4" class="Form-Columns-Dense">
              <InputField
                type="number"
                label="TAX (%)"
                readonly
                :clearable="false"
                hide-details="auto"
                v-model="chargeDetails.tax_percentage"
              />
            </v-col>
            <v-col cols="12" lg="4" class="Form-Columns-Dense">
              <InputField
                type="number"
                :label="`Rate (${$globals.currency}) *`"
                :disabled="!chargeDetails.charge"
                :rules="[
                  (val) => !!val || 'Rate is Required',
                  (val) => val > 0 || 'Rate can not be less than or equal to 0',
                ]"
                hide-details="auto"
                v-model="chargeDetails.rate"
                :error-messages="formErrors.rate"
                @input="delete formErrors.rate"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-5">
        <v-btn
          :disabled="!isValid"
          class="ml-auto"
          @click="submitForm"
          depressed
          color="primary"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InputField from "@/components/FormBaseComponents/InputField.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import { TaxTypes } from "@/utils/choices";

export default {
  components: {
    InputField,
    MultiSelect,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    contractId: {
      type: Number,
    },
    editCharge: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.getCharges();
        this.chargeDetails = { ...this.editCharge };
        this.selectedCharge = this.chargeDetails.charge
          ? {
              id: this.chargeDetails.charge,
              charge_title: this.chargeDetails.charge_title,
            }
          : null;
      }
    },
  },
  data() {
    return {
      chargeDetails: {},
      selectedCharge: null,
      formErrors: {},
      allCharges: [],
      taxTypeList: TaxTypes,
      isValid: true,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    getCharges() {
      this.$api.charge
        .getChargeSelectList({
          limit: "all",
          is_active: "true",
          is_system_generated: "false",
          is_revenue: "true",
        })
        .then((res) => {
          this.allCharges = res.data;
        })
        .catch(() => {});
    },
    setChargeDetails(val) {
      if (!val) {
        this.chargeDetails = {};
        return;
      }
      this.chargeDetails.charge = val.id;
      this.chargeDetails.charge_title = val.charge_title;
      this.chargeDetails.gl_no = val.gl_no;
      this.chargeDetails.description = val.description;
      this.chargeDetails.arabic_description = val.arabic_description;
      this.chargeDetails.da_charge_type = "Revenue";
      this.chargeDetails.rate = val.rate;
      this.chargeDetails.tax_percentage = val.tax_percentage;
      this.chargeDetails.tax_type = val.tax_type;
    },
    submitForm() {
      this.$bus.$emit("showLoader", true);
      let data = { ...this.chargeDetails };
      data.customer_contract_v2 = this.contractId;

      const request = data.id
        ? this.$api.customerContractV2.updateCustomerContractV2Charge({
            id: data.id,
            data: data,
          })
        : this.$api.customerContractV2.addCustomerContractV2Charge(data);

      request
        .then(() => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Charge saved Successfully!",
            color: "success",
          });
          this.resetForm();
          this.showDialog = false;
          this.$emit("submitted");
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: `${
              err.data && err.data.non_field_errors
                ? err.data.non_field_errors.join()
                : "Couldn't save Charge!"
            }`,
            color: "error",
          });
          this.formErrors = { ...this.formErrors, ...err.data };
        });
    },
    resetForm() {
      if (this.$refs.chargeForm) {
        this.$refs.chargeForm.reset();
      }
      this.formErrors = {};
      this.chargeDetails = {};
      this.selectedCharge = null;
    },
  },
};
</script>
